// eve-chat-store — Multi-Page-Chat. Jede Page hat eigene Chat-History
// (pageKey → ChatMessage[]). EVE ist allgegenwärtig: auf jeder Page eigener
// Verlauf, aber zentral persistiert (D1-Sync später, heute localStorage).
//
// Doc-Anweisung 2026-05-15: „SIE IST ALLGEGENWÄRTIG"
// Page-Keys = location.pathname (z.B. '/markt', '/markt/polymarket', '/trading').

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const EVE_CHAT_STORE_KEY = 'eve_chat_v3_multi';
export const EVE_CHAT_MAX_ENTRIES_PER_PAGE = 200;

export type ChatRole = 'user' | 'assistant';

export interface Citation {
  /** Volle URL. */
  url: string;
  /** Optionaler Titel (z.B. der Seitentitel). */
  title?: string;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  /** ISO-Timestamp */
  createdAt: string;
  text: string;
  /** Welcher Agent ausgelöst hat (nur für assistant). */
  agentKey?: string;
  /** Während Streaming true. */
  streaming?: boolean;
  /** Wenn etwas schief lief. */
  error?: string;
  /** Quellen-URLs aus xAI Live-Search (assistant-only). Werden vom Worker
   *  als `data: {"citation":{...}}` SSE-Frames gepiped und im Drawer als
   *  Quellen-Liste unter der Antwort gerendert. */
  citations?: Citation[];
}

export interface EveChatState {
  /** pageKey → Messages */
  byPage: Record<string, ChatMessage[]>;
}

export interface EveChatActions {
  push: (pageKey: string, msg: ChatMessage) => void;
  appendToLast: (pageKey: string, chunk: string) => void;
  appendCitation: (pageKey: string, citation: Citation) => void;
  finishStreaming: (pageKey: string) => void;
  markError: (pageKey: string, error: string) => void;
  /** Entfernt die letzte leere Assistant-Platzhalter-Bubble (z.B. wenn ein
   *  Call still abgebrochen wurde und ein Modal die Rückmeldung übernimmt). */
  removeLastIfEmptyAssistant: (pageKey: string) => void;
  clear: (pageKey: string) => void;
  clearAll: () => void;
}

export type EveChatStore = EveChatState & EveChatActions;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createChatMessage(input: {
  role: ChatRole;
  text: string;
  agentKey?: string;
  streaming?: boolean;
}): ChatMessage {
  return {
    id: newId(),
    role: input.role,
    createdAt: new Date().toISOString(),
    text: input.text,
    agentKey: input.agentKey,
    streaming: input.streaming,
  };
}

export const useEveChatStore = create<EveChatStore>()(
  persist(
    (set) => ({
      byPage: {},

      push: (pageKey, msg) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          const next = [...current, msg].slice(-EVE_CHAT_MAX_ENTRIES_PER_PAGE);
          return { byPage: { ...s.byPage, [pageKey]: next } };
        }),

      appendToLast: (pageKey, chunk) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          if (current.length === 0) return s;
          const last = current[current.length - 1];
          if (!last || last.role !== 'assistant') return s;
          const updated: ChatMessage = { ...last, text: last.text + chunk };
          return {
            byPage: {
              ...s.byPage,
              [pageKey]: [...current.slice(0, -1), updated],
            },
          };
        }),

      appendCitation: (pageKey, citation) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          if (current.length === 0) return s;
          const last = current[current.length - 1];
          if (!last || last.role !== 'assistant') return s;
          // Dedupe by URL — Das LLM kann dieselbe Quelle mehrfach zitieren.
          const existing = last.citations ?? [];
          if (existing.some((c) => c.url === citation.url)) return s;
          const updated: ChatMessage = {
            ...last,
            citations: [...existing, citation],
          };
          return {
            byPage: {
              ...s.byPage,
              [pageKey]: [...current.slice(0, -1), updated],
            },
          };
        }),

      finishStreaming: (pageKey) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          if (current.length === 0) return s;
          const last = current[current.length - 1];
          if (!last || last.role !== 'assistant') return s;
          const updated: ChatMessage = { ...last, streaming: false };
          return {
            byPage: {
              ...s.byPage,
              [pageKey]: [...current.slice(0, -1), updated],
            },
          };
        }),

      markError: (pageKey, error) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          if (current.length === 0) return s;
          const last = current[current.length - 1];
          if (!last || last.role !== 'assistant') return s;
          const updated: ChatMessage = {
            ...last,
            streaming: false,
            error,
            text: last.text.length > 0 ? last.text : `⚠ ${error}`,
          };
          return {
            byPage: {
              ...s.byPage,
              [pageKey]: [...current.slice(0, -1), updated],
            },
          };
        }),

      removeLastIfEmptyAssistant: (pageKey) =>
        set((s) => {
          const current = s.byPage[pageKey] ?? [];
          if (current.length === 0) return s;
          const last = current[current.length - 1];
          if (!last || last.role !== 'assistant' || last.text.length > 0) return s;
          return {
            byPage: {
              ...s.byPage,
              [pageKey]: current.slice(0, -1),
            },
          };
        }),

      clear: (pageKey) =>
        set((s) => {
          const next = { ...s.byPage };
          delete next[pageKey];
          return { byPage: next };
        }),

      clearAll: () => set({ byPage: {} }),
    }),
    {
      name: EVE_CHAT_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persisted: unknown, version: number) => {
        // Migration vom alten Single-Page-Schema (messages: ChatMessage[])
        // auf das neue Multi-Page-Schema (byPage: Record<string, ChatMessage[]>).
        // Alte Messages wandern unter pageKey '/markt'.
        if (version < 2 && persisted && typeof persisted === 'object') {
          const old = persisted as { messages?: ChatMessage[] };
          if (Array.isArray(old.messages)) {
            return { byPage: { '/markt': old.messages } } as EveChatState;
          }
        }
        // Fallback: leerer State.
        if (!persisted || typeof persisted !== 'object' || !('byPage' in persisted)) {
          return { byPage: {} } as EveChatState;
        }
        return persisted as EveChatState;
      },
    },
  ),
);

/**
 * Selector: Messages für eine konkrete Page.
 *
 * WICHTIG: konstantes `EMPTY` statt `?? []` damit zustand bei jedem Render
 * dieselbe Array-Reference sieht und nicht in Re-Render-Loops kommt.
 * Die zurückgegebene Selector-Funktion wird intern via Map gecached pro pageKey
 * — sonst hätte jeder Render eine neue Selector-Funktion, was auch
 * Re-Render-Loops triggern könnte (React-19 strict-mode greift hart durch).
 */
const EMPTY_MESSAGES: ReadonlyArray<ChatMessage> = [];
const selectorCache = new Map<string, (s: EveChatState) => ReadonlyArray<ChatMessage>>();

export function selectMessagesFor(
  pageKey: string,
): (s: EveChatState) => ReadonlyArray<ChatMessage> {
  let cached = selectorCache.get(pageKey);
  if (!cached) {
    cached = (s: EveChatState) => s.byPage[pageKey] ?? EMPTY_MESSAGES;
    selectorCache.set(pageKey, cached);
  }
  return cached;
}
